/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soyun Lee (sylee2@rsupport.com)
 *
 * @description
 *
 * @created at Thu Apr 10 2025
 **/

import { useRef, useState } from "react";

import { Column, Icon, TabContent, Typography } from "@/frameworks/designStuff/components";
import { useEmojiManager } from "@/frameworks/core/chat/useEmojiManager";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";

import * as S from "../private/emotion";

export const iconCategoryMap = [
	{ icon: "Clock24", category: "recent" },
	{ icon: "Emoji24", category: "smileys_emotion" },
	{ icon: "Action24", category: "people_body" },
	{ icon: "Bird24", category: "animals_nature" },
	{ icon: "Carrot24", category: "food_drink" },
	{ icon: "Airplane24", category: "travel_places" },
	{ icon: "FollowRun24", category: "activities" },
	{ icon: "Favorite24", category: "objects" },
	{ icon: "Things24", category: "symbols" },
	{ icon: "Flag24", category: "flags" }
];

export default function SystemEmoticon({
	onSelectEmoticon
}: {
	onSelectEmoticon: (emoticon: { name: string; path?: string }) => void;
}) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [activeCategory, setActiveCategory] = useState("recent");
	const { activeEmojis, saveRecentEmoji } = useEmojiManager(activeCategory);

	containerRef.current?.scrollTo({ top: 0, behavior: "auto" });

	return (
		<TabContent activeId="emoji" key="emoji">
			<div ref={containerRef} css={[hoverScrollCss()]} style={{ height: "179px" }}>
				<Column>
					{activeEmojis.map((category) => (
						<Column gap={8} key={category.slug} id={category.slug}>
							<Typography typoSize="Title2" style={{ marginTop: "16px" }}>
								{category.name}
							</Typography>
							<div>
								{category.emojis.map((emoji) => (
									<S.Emoji
										key={emoji.slug}
										className="chat-font"
										onClick={() => {
											onSelectEmoticon?.({ name: emoji.emoji });
											saveRecentEmoji(emoji.slug);
										}}
									>
										{emoji.emoji}
									</S.Emoji>
								))}
							</div>
						</Column>
					))}
				</Column>
			</div>
			<S.InfoEmoji gap={2.2} horizonAlign="center">
				{iconCategoryMap.map(({ icon, category }) => (
					<S.EmojiTitle
						key={icon}
						isClicked={category === activeCategory}
						onClick={() => setActiveCategory(category)}
						horizonAlign="center"
						verticalAlign="center"
					>
						<Icon name={icon as DesignStuff.Icon.Name} size={"20"} />
					</S.EmojiTitle>
				))}
			</S.InfoEmoji>
		</TabContent>
	);
}
